import clsx from "clsx";

/**
 * Shared surface and text tokens for Home, Review, Agentic, and Workbench modules.
 * Always pair light + dark utilities. Prefer importing from domain modules
 * (homeSurfaces, reviewSurfaces, agenticSurface, workbenchSurface) when a
 * domain-specific alias exists.
 */

/**
 * Translucent raised panel — dark mode only (Home widgets).
 * No backdrop-filter: these render inside frosted aniOS windows, and nested
 * blurs in repeated cards force a full re-blur on every repaint (hover flashing).
 */
export const SURFACE_TRANSLUCENT_DARK =
  "dark:border dark:border-white/[0.08] dark:bg-slate-900/75 dark:shadow-[inset_0_1px_0_rgba(255,255,255,0.06)]";

const shadowSm = "shadow-sm dark:shadow-md dark:shadow-black/35";

// ── Surfaces ────────────────────────────────────────────────────────────────

export const SURFACE_PAGE = clsx("bg-slate-100", "dark:bg-transparent");

export const SURFACE_RAISED = clsx("bg-white", SURFACE_TRANSLUCENT_DARK);

export const SURFACE_RAISED_SOLID = clsx(
  "bg-white",
  shadowSm,
  "dark:bg-slate-800",
);

export const SURFACE_INSET = clsx(
  "bg-slate-50",
  "dark:bg-black/20",
);

export const SURFACE_FEATURED = "bg-indigo-50 dark:bg-indigo-500/15";

export const SURFACE_HOVER =
  "transition-colors hover:bg-slate-200/90 dark:hover:bg-white/[0.08]";

export const SURFACE_SELECTED = "bg-indigo-500/15 ring-1 ring-indigo-400/50";

export const SURFACE_MATRIX_TOOLBAR = clsx(
  "bg-slate-100",
  "dark:bg-white/[0.04]",
);

export const SURFACE_MATRIX_ROW_ODD = "bg-slate-50 dark:bg-white/[0.03]";

export const SURFACE_MATRIX_ROW_EVEN = clsx("bg-slate-100", "dark:bg-transparent");

export const SURFACE_NOTIFICATION_ROW =
  "rounded-xl bg-slate-50 px-3 py-2.5 shadow-sm dark:bg-white/[0.05] dark:shadow-[inset_0_1px_0_rgba(255,255,255,0.06)]";

export const SURFACE_DROPDOWN_PANEL = clsx(
  "rounded-xl border border-slate-200 bg-white p-1.5 shadow-lg",
  "dark:border-slate-700/80 dark:bg-slate-900 dark:shadow-black/30",
);

export const SURFACE_ICON_BTN =
  "border border-slate-200 bg-slate-100 text-slate-500 dark:border-slate-700/80 dark:bg-slate-900/60";

export const SURFACE_SKELETON = "animate-pulse bg-slate-200 dark:bg-slate-900/40";

// ── Text roles ──────────────────────────────────────────────────────────────

export const TEXT_TITLE = "text-base font-semibold text-slate-900 dark:text-slate-100";

export const TEXT_HEADING =
  "text-lg font-semibold leading-snug text-slate-900 dark:text-slate-50 sm:text-xl";

export const TEXT_HERO =
  "text-[15px] font-semibold leading-snug text-slate-900 dark:text-slate-100";

export const TEXT_BODY = "text-sm text-slate-600 dark:text-slate-300";

export const TEXT_MUTED = "text-slate-500 dark:text-slate-400";

export const TEXT_ROW_TITLE = "text-sm font-medium text-slate-900 dark:text-slate-100";

export const TEXT_ROW_SUBTITLE = "text-xs text-slate-500 dark:text-slate-400";

export const TEXT_ROOT = "text-slate-900 dark:text-white";

export const TEXT_SUBTLE = "text-slate-600 dark:text-slate-400";

// ── Borders & dividers ──────────────────────────────────────────────────────

export const BORDER_DIVIDER = "border-slate-200 dark:border-slate-700/60";

export const BORDER_SUBTLE = "border-slate-200 dark:border-white/10";

export const LIST_SHELL = clsx(
  "divide-y divide-slate-200 rounded-lg border border-slate-200",
  "dark:divide-slate-800/50 dark:border-slate-800/60",
);

export const FOCUS_RING =
  "focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-indigo-400/30";

// ── Interactive ───────────────────────────────────────────────────────────────

export const ROW_HOVER = clsx(
  "hover:bg-slate-100 dark:hover:bg-slate-800/50",
  FOCUS_RING,
);

export const LINK_DEFAULT =
  "text-xs text-slate-600 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-200";

export const LINK_INDIGO =
  "text-indigo-600 hover:text-indigo-700 dark:text-indigo-300 dark:hover:text-indigo-200";

export const FILTER_BUTTON = clsx(
  "inline-flex h-8 min-h-8 shrink-0 items-center justify-center gap-1.5 rounded-xl px-2.5 text-slate-500 transition-colors",
  "hover:bg-slate-50 hover:text-slate-700",
  "dark:hover:bg-slate-800/80 dark:hover:text-slate-200",
  FOCUS_RING,
);

export const COUNT_BADGE =
  "rounded-full bg-slate-200 px-2 py-0.5 text-xs font-medium text-slate-700 dark:bg-slate-700/80 dark:text-slate-300";

// ── Status palette ────────────────────────────────────────────────────────────

export const STATUS_SUCCESS_TEXT = "text-emerald-700 dark:text-emerald-400";

export const STATUS_SUCCESS_BG = "bg-emerald-50 dark:bg-emerald-500/10";

export const STATUS_WARNING_TEXT = "text-amber-700 dark:text-amber-400";

export const STATUS_WARNING_BG = "bg-amber-50 dark:bg-amber-500/10";

/**
 * Status pill / count badge / icon ring — matches Badge `status*` base colours.
 * Keep SURFACE and RING aliases in sync; badges add hover states on top.
 */
export const STATUS_WARNING_SURFACE =
  "bg-amber-400/20 text-amber-700 dark:bg-amber-400/10 dark:text-amber-400";

export const STATUS_DANGER_TEXT = "text-red-700 dark:text-red-400";

export const STATUS_DANGER_BG = "bg-red-50 dark:bg-red-500/10";

export const STATUS_DANGER_SURFACE =
  "bg-red-500/15 text-red-700 dark:bg-red-500/10 dark:text-red-400";

export const STATUS_INFO_TEXT = "text-slate-600 dark:text-slate-400";

export const STATUS_INFO_BG = "bg-slate-100 dark:bg-white/5";

export const STATUS_INFO_SURFACE =
  "bg-slate-500/18 text-slate-700 dark:bg-slate-700/58 dark:text-slate-300";

export const STATUS_AGENT_TEXT = "text-indigo-700 dark:text-indigo-300";

export const STATUS_AGENT_BG = "bg-indigo-50 dark:bg-indigo-500/15";

export const STATUS_AGENT_SURFACE =
  "bg-indigo-400/15 text-indigo-700 dark:bg-indigo-400/10 dark:text-indigo-400";

export const STATUS_SUCCESS_SURFACE =
  "bg-emerald-500/15 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-400";

export const STATUS_WARNING_RING = STATUS_WARNING_SURFACE;

export const STATUS_SUCCESS_RING = STATUS_SUCCESS_SURFACE;

export const STATUS_DANGER_RING = STATUS_DANGER_SURFACE;

export const STATUS_INFO_RING = STATUS_INFO_SURFACE;

export const STATUS_AGENT_RING = STATUS_AGENT_SURFACE;

export const STATUS_HUMAN_TEXT = "text-slate-700 dark:text-slate-300";

export const STATUS_HUMAN_BG = "bg-slate-100 dark:bg-white/5";

// ── Diff (HITL edit review) ─────────────────────────────────────────────────

export const DIFF_REMOVED =
  "bg-red-100 text-red-800 line-through decoration-red-600/60 dark:bg-red-500/15 dark:text-red-300";

export const DIFF_ADDED =
  "bg-emerald-100 text-emerald-800 dark:bg-emerald-500/15 dark:text-emerald-300";

// ── Admin banner ────────────────────────────────────────────────────────────

export const ADMIN_BANNER = clsx(
  "mb-3 flex flex-col gap-3 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3",
  "dark:border-transparent dark:bg-amber-300/15 sm:flex-row sm:items-center sm:justify-between",
);

export const ADMIN_BANNER_TEXT = "text-sm text-amber-950 dark:text-amber-100/90";

export const ADMIN_BANNER_EMPHASIS = "font-medium text-amber-900 dark:text-amber-50";

export const ADMIN_BANNER_BTN =
  "cursor-pointer rounded-lg bg-amber-200/80 px-3 py-1.5 text-xs font-medium text-amber-950 hover:bg-amber-300/80 dark:bg-amber-950/25 dark:text-amber-100 dark:hover:bg-amber-950/35";

// ── Layout (spacing, radius, card anatomy) ──────────────────────────────────

export {
  CARD_FOOTER,
  CARD_HEADER,
  CONTEXT_RAIL_WIDTH,
  GAP_INLINE,
  GAP_INLINE_TIGHT,
  GAP_STACK,
  GAP_STACK_LOOSE,
  GAP_SURFACE,
  PAD_BAR,
  PAD_CARD,
  PAD_INSET,
  PAD_ROW,
  RADIUS_CHIP,
  RADIUS_INSET,
  RADIUS_ROW,
  RADIUS_SURFACE,
  SPACE_PAGE,
  SURFACE_ACTION_BAR,
  TEXT_BODY_MUTED,
  TEXT_EYEBROW,
  TEXT_EYEBROW_ACCENT,
  TEXT_META,
  TEXT_STAT,
} from "./layoutTokens";
